/**
 * userApi — Symfony API client for user-scoped (NOT org-scoped) endpoints.
 *
 * This module is the canonical surface for the bootstrap data slots that the
 * new React calendar pages (Phase 1+) need before any view-specific data can
 * be fetched. It deliberately uses **named function exports** instead of the
 * legacy `class extends BaseApi` pattern so the bootstrap thunk can import
 * the bare functions and Jest can mock them at the module level.
 *
 * Each function returns the raw `BaseApi.get()` promise — no payload
 * unwrapping happens here. The slice that consumes the response is the right
 * place for shape normalization, because different views may want to read
 * different subsets of the same payload.
 *
 * Endpoints (verified 2026-04-08):
 *   - GET `/user/profile`        → UserProfileResponseDto (user + timezone)
 *   - GET `/user/organizations`  → list of orgs the user belongs to
 *
 * @module userApi
 */

import BaseApi from './BaseApi.js';

/**
 * GET /api/v1/user/profile — current user profile (id, name, email, timezone,
 * angularDateFormat, angularTimeFormat, etc.). Symfony route lives in
 * `symphony-holdshort/src/Controller/UserController.php`.
 *
 * @returns {Promise<Object>} Raw response — slice is responsible for shape
 *   normalization.
 */
export function getUserProfile() {
	return BaseApi.get('/user/profile');
}

/**
 * GET /api/v1/user/organizations — every organization the current user has
 * access to, with their per-org rights. The optional `activeOnly` flag is
 * preserved from the legacy `DashboardApi.getUserOrganizations` so the
 * endpoint works the same way regardless of which client calls it.
 *
 * @param {boolean} [activeOnly=false]
 * @returns {Promise<Object>}
 */
export function getUserOrganizations(activeOnly = false) {
	const query = activeOnly ? '?activeOnly=true' : '';
	return BaseApi.get(`/user/organizations${query}`);
}

/**
 * GET /api/v1/user/settings — current user's per-user preference overrides.
 * Backed by `UserSettingsResponseDto`. Notably exposes `eventDefaultLength`
 * (seconds). A returned `0` means "no per-user override" — consumers must
 * fall back to the org-level default in that case.
 *
 * @returns {Promise<Object>} Raw response — slice is responsible for shape
 *   normalization.
 */
export function getUserSettings() {
	return BaseApi.get('/user/settings');
}

/**
 * GET /api/v1/user/calendar-feed — the current user's calendar feed
 * subscription state (`{ state: 'none'|'pending'|'active', createdAt,
 * lastFetchedAt, fetchCount }`). Does NOT return the feed URL itself.
 *
 * @returns {Promise<Object>} Raw response — slice is responsible for shape
 *   normalization.
 */
export function getCalendarFeedStatus() {
	return BaseApi.get('/user/calendar-feed');
}

/**
 * GET /api/v1/user/calendar-feed/url — the current user's calendar feed URL,
 * generating one on the first call. Returns the same URL on every later
 * call, so adding the calendar to another device never breaks the ones
 * already subscribed. Treat the value as a credential — anyone holding it
 * can read the schedule.
 *
 * @returns {Promise<Object>} Raw response — caller is responsible for shape
 *   normalization.
 */
export function getCalendarFeedUrl() {
	return BaseApi.get('/user/calendar-feed/url');
}

/**
 * POST /api/v1/user/calendar-feed/rotate — issues a new calendar feed URL
 * and invalidates the previous one immediately. Every calendar already
 * subscribed stops updating until it's re-added with the new URL.
 *
 * @returns {Promise<Object>} Raw response — caller is responsible for shape
 *   normalization.
 */
export function rotateCalendarFeedUrl() {
	return BaseApi.post('/user/calendar-feed/rotate', {});
}

/**
 * DELETE /api/v1/user/calendar-feed — deletes the current user's calendar
 * feed token. The link stops working everywhere immediately; subscribing
 * again afterwards issues a different link, unrelated to the one just
 * revoked. No request body.
 *
 * @returns {Promise<Object>} Raw response — caller is responsible for shape
 *   normalization.
 */
export function unsubscribeCalendarFeed() {
	return BaseApi.delete('/user/calendar-feed');
}
